// ==================== SUBSCRIPTION SERVICE ====================

import { apiService } from './api';
import type { ApiResponse } from '../types';

// ==================== TYPES ====================

export interface CouponSummary {
  code: string;
  discountPercent: number;
  discountScope: 'PLAN_ONLY' | 'WHOLE_PAYMENT';
  discountDuration: 'FIRST_PAYMENT' | 'EVERY_PAYMENT';
  isNew: boolean;
  appliesToThisPayment: boolean;
}

export interface UpgradeQuote {
  planAmount: number;
  leadGenAddon: number;
  totalAmount: number;
  recurringAmount: number;
  discountAmount: number;
  amountDueNow: number;
  nextRenewalAmount: number;
  coupon: CouponSummary | null;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  organizationCount: number;
  planName: string;
}

export interface PriceCalculation {
  planAmount: number;
  setupFee: number;
  leadGenAddon: number;
  totalAmount: number;
  recurringAmount: number;
  discountAmount?: number;
  amountDueNow?: number;
  nextRenewalAmount?: number;
  coupon?: CouponSummary | null;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  planName: string;
  setupFeeIncluded: boolean;
  leadGenAddonIncluded: boolean;
  /** Multiple organizations: planAmount and setupFee are totals for this many */
  organizationCount?: number;
  planUnitAmount?: number;
  setupFeeUnitAmount?: number;
}

export interface SetupFeeDetails {
  amount: number;
  enabled: boolean;
}

export interface LeadGenAddonDetails {
  /** Monthly add-on fee for the plan. */
  amount: number;
  enabled: boolean;
}

/** What enabling the add-on costs right now for the current subscription. */
export interface LeadGenAddonQuote {
  monthlyFee: number;
  billingCycle: 'MONTHLY' | 'YEARLY';
  currency: string;
  /** Charged now for the rest of the current period (0 if already paid). */
  chargeNow: number;
  discountAmount?: number;
  amountDue?: number;
  coupon?: CouponSummary | null;
  monthsCharged: number;
  /** Added to every renewal while enabled (12 months on a yearly plan). */
  recurringAmount: number;
  /** The whole next renewal with the add-on on, after any recurring coupon discount */
  nextRenewalAmount?: number;
  periodEnd: string;
  alreadyPaidThisPeriod: boolean;
}

/** What buying more organizations costs now (plan price + optional setup fee each). */
export interface OrganizationsQuote {
  count: number;
  /** Plan price per organization for the subscription's billing cycle */
  unitAmount: number;
  /** The plan's setup fee per organization (charged only when chosen) */
  setupFeeUnitAmount: number;
  setupFeeAvailable: boolean;
  setupFeeIncluded: boolean;
  /** Charged now */
  chargeNow: number;
  discountAmount?: number;
  amountDue?: number;
  coupon?: CouponSummary | null;
  /** Added to every renewal */
  recurringIncrease: number;
  /** The whole next renewal after this purchase, after any recurring coupon discount */
  nextRenewalAmount?: number;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  /** Organizations purchased before this purchase */
  organizationCount: number;
}

/** Add-on amount billed per cycle: the monthly fee, ×12 on yearly plans. */
export const leadGenAddonCycleAmount = (monthlyFee: number, isYearly: boolean) =>
  Math.round((isYearly ? monthlyFee * 12 : monthlyFee) * 100) / 100;

export interface CheckoutSessionResponse {
  checkoutSessionId: string;
  clientSecret: string;
  subscriptionId: string;
  priceDetails: PriceCalculation;
}

export interface PaymentMethod {
  id: string;
  stripePaymentMethodId: string;
  type: 'CARD' | 'BANK_ACCOUNT' | 'WALLET';
  brand: string | null;
  last4: string;
  expiryMonth: number | null;
  expiryYear: number | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface Subscription {
  id: string;
  status: 'PENDING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'EXPIRED';
  billingCycle: 'MONTHLY' | 'YEARLY';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextBillingDate: string;
  planAmount: number;
  setupFee: number;
  setupFeeIncluded: boolean;
  leadGenAddonEnabled: boolean;
  leadGenAddonAmount: number;
  totalAmount: number;
  recurringAmount: number;
  nextRenewalAmount?: number;
  coupon?: CouponSummary | null;
  /** Organizations purchased (plan price is charged per organization) */
  organizationCount?: number;
  currency: string;
  autoRenew: boolean;
  plan: {
    id: string;
    name: string;
    description: string | null;
    monthlyPrice: number;
    yearlyPrice: number | null;
  };
  defaultPaymentMethod: PaymentMethod | null;
  payments: Array<{
    id: string;
    amount: number;
    status: string;
    paidAt: string | null;
    createdAt: string;
  }>;
}

// ==================== SERVICE ====================

export const subscriptionService = {
  /**
   * Calculate price for a subscription
   */
  calculatePrice: async (params: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    includeSetupFee?: boolean;
    includeLeadGenAddon?: boolean;
    /** Multiple organizations; only sent when more than one */
    organizationCount?: number;
    couponCode?: string;
  }): Promise<ApiResponse<PriceCalculation>> => {
    const queryParams = new URLSearchParams({
      planId: params.planId,
      billingCycle: params.billingCycle,
    });
    if (params.includeSetupFee) queryParams.set('includeSetupFee', 'true');
    if (params.includeLeadGenAddon) queryParams.set('includeLeadGenAddon', 'true');
    if (params.couponCode) queryParams.set('couponCode', params.couponCode);
    if (params.organizationCount && params.organizationCount > 1) {
      queryParams.append('organizationCount', String(params.organizationCount));
    }
    return apiService.get<PriceCalculation>(`/customer/subscription/calculate-price?${queryParams}`);
  },

  /**
   * Get setup fee details for a specific plan
   */
  getSetupFeeDetails: async (planId?: string): Promise<ApiResponse<SetupFeeDetails>> => {
    const queryParams = planId ? `?planId=${planId}` : '';
    return apiService.get<SetupFeeDetails>(`/customer/subscription/setup-fee${queryParams}`);
  },

  /**
   * Lead Generation add-on (Lead Ops) — amount / availability from selected plan (DB), same pattern as setup fee.
   */
  getLeadGenAddonDetails: async (planId?: string): Promise<ApiResponse<LeadGenAddonDetails>> => {
    const queryParams = planId ? `?planId=${planId}` : '';
    return apiService.get<LeadGenAddonDetails>(`/customer/subscription/lead-gen-addon${queryParams}`);
  },

  /**
   * Create a checkout session
   */
  createCheckout: async (params: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    includeSetupFee?: boolean;
    includeLeadGenAddon?: boolean;
    /** Multiple organizations; only sent when more than one */
    organizationCount?: number;
    couponCode?: string;
  }): Promise<ApiResponse<CheckoutSessionResponse>> => {
    const { organizationCount, ...rest } = params;
    return apiService.post<CheckoutSessionResponse>('/customer/subscription/checkout', {
      ...rest,
      ...(organizationCount && organizationCount > 1 && { organizationCount }),
    });
  },

  /**
   * Handle payment success callback
   */
  handlePaymentSuccess: async (sessionId: string): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/payment-success', { sessionId });
  },

  /**
   * Get current subscription
   */
  getCurrentSubscription: async (): Promise<ApiResponse<Subscription | null>> => {
    return apiService.get<Subscription | null>('/customer/subscription/current');
  },

  /**
   * Cancel subscription
   */
  cancelSubscription: async (reason?: string): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/cancel', { reason });
  },

  /**
   * Resume a cancelled subscription (before period ends)
   */
  resumeSubscription: async (): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/resume');
  },

  /**
   * Get payment methods
   */
  getPaymentMethods: async (): Promise<ApiResponse<PaymentMethod[]>> => {
    return apiService.get<PaymentMethod[]>('/customer/subscription/payment-methods');
  },

  /**
   * Add a new payment method
   */
  addPaymentMethod: async (params: {
    paymentMethodId: string;
    setAsDefault?: boolean;
  }): Promise<ApiResponse<PaymentMethod>> => {
    return apiService.post<PaymentMethod>('/customer/subscription/payment-methods', params);
  },

  /**
   * Set default payment method
   */
  setDefaultPaymentMethod: async (paymentMethodId: string): Promise<ApiResponse<void>> => {
    return apiService.post<void>(`/customer/subscription/payment-methods/${paymentMethodId}/default`);
  },

  /**
   * Remove a payment method
   */
  removePaymentMethod: async (paymentMethodId: string): Promise<ApiResponse<void>> => {
    return apiService.delete<void>(`/customer/subscription/payment-methods/${paymentMethodId}`);
  },

  /**
   * Create setup intent for adding new payment method
   */
  createSetupIntent: async (): Promise<ApiResponse<{ clientSecret: string }>> => {
    return apiService.post<{ clientSecret: string }>('/customer/subscription/payment-methods/setup-intent');
  },

  /**
   * Get payment history
   */
  getPaymentHistory: async (params?: { page?: number; limit?: number }): Promise<ApiResponse<PaymentHistoryResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    const query = queryParams.toString() ? `?${queryParams}` : '';
    return apiService.get<PaymentHistoryResponse>(`/customer/subscription/payments${query}`);
  },

  /**
   * Get single payment details
   */
  getPaymentDetails: async (paymentId: string): Promise<ApiResponse<PaymentDetails>> => {
    return apiService.get<PaymentDetails>(`/customer/subscription/payments/${paymentId}`);
  },

  /**
   * Get invoice for a payment
   */
  getInvoice: async (paymentId: string): Promise<ApiResponse<Invoice>> => {
    return apiService.get<Invoice>(`/customer/subscription/payments/${paymentId}/invoice`);
  },

  /**
   * Toggle auto-renewal
   */
  toggleAutoRenew: async (autoRenew: boolean): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/auto-renew', { autoRenew });
  },

  /**
   * What enabling the Lead Generation add-on would charge now for the current subscription.
   */
  getLeadGenAddonQuote: async (couponCode?: string): Promise<ApiResponse<LeadGenAddonQuote>> => {
    const query = couponCode ? `?${new URLSearchParams({ couponCode })}` : '';
    return apiService.get<LeadGenAddonQuote>(`/customer/subscription/lead-gen-addon/quote${query}`);
  },

  /**
   * Enable or disable the Lead Generation add-on (priced monthly).
   * Enabling charges the rest of the current period now (see getLeadGenAddonQuote),
   * then the add-on is billed with every renewal.
   */
  toggleLeadGenAddon: async (
    enabled: boolean,
    paymentMethodId?: string,
    couponCode?: string,
  ): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/lead-gen-addon', {
      enabled,
      ...(paymentMethodId && { paymentMethodId }),
      ...(couponCode && { couponCode }),
    });
  },

  /**
   * What buying more organizations would charge now for the current subscription.
   */
  getOrganizationsQuote: async (
    count: number,
    includeSetupFee: boolean,
    couponCode?: string,
  ): Promise<ApiResponse<OrganizationsQuote>> => {
    const queryParams = new URLSearchParams({
      count: String(count),
      includeSetupFee: String(includeSetupFee),
    });
    if (couponCode) queryParams.set('couponCode', couponCode);
    return apiService.get<OrganizationsQuote>(`/customer/subscription/organizations/quote?${queryParams}`);
  },

  /**
   * Buy more organizations: charges the selected saved card now, then every
   * renewal bills the plan price for each organization.
   */
  buyOrganizations: async (params: {
    count: number;
    includeSetupFee: boolean;
    paymentMethodId?: string;
    couponCode?: string;
  }): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/organizations', {
      count: params.count,
      includeSetupFee: params.includeSetupFee,
      ...(params.paymentMethodId && { paymentMethodId: params.paymentMethodId }),
      ...(params.couponCode && { couponCode: params.couponCode }),
    });
  },

  /**
   * What changing plan would charge now (the Upgrade page pays only a matching quote)
   */
  getUpgradeQuote: async (params: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    couponCode?: string;
  }): Promise<ApiResponse<UpgradeQuote>> => {
    const query = new URLSearchParams({ planId: params.planId, billingCycle: params.billingCycle });
    if (params.couponCode) query.set('couponCode', params.couponCode);
    return apiService.get<UpgradeQuote>(`/customer/subscription/upgrade/quote?${query}`);
  },

  /**
   * Upgrade/downgrade plan (one-click payment with saved payment method)
   */
  upgradePlan: async (params: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    paymentMethodId: string;
    couponCode?: string;
  }): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/upgrade', params);
  },
};

// ==================== ADDITIONAL TYPES ====================

export interface PaymentDetails {
  id: string;
  amount: number;
  planAmount: number | null;
  setupFee: number | null;
  leadGenAddonFee: number | null;
  currency: string;
  status: string;
  type: 'SUBSCRIPTION' | 'RENEWAL' | 'UPGRADE' | 'ADDON' | 'MANUAL' | 'ORGANIZATION';
  stripePaymentIntentId: string | null;
  paidAt: string | null;
  createdAt: string;
  paymentMethod: PaymentMethod | null;
  /** Snapshot at time of payment (use for display so history shows correct plan) */
  planName?: string | null;
  billingCycle?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  subscription: {
    id: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    plan: {
      id: string;
      name: string;
      description: string | null;
    } | null;
  };
}

export interface PaymentHistoryResponse {
  items: PaymentDetails[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface Invoice {
  invoiceNumber: string;
  invoiceDate: string;
  paidDate: string | null;
  status: string;
  customer: {
    name: string;
    email: string;
    address: string;
    phone: string;
  };
  company: {
    name: string;
    tagline: string;
    website: string;
    email: string;
  };
  plan: {
    name: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    description: string;
  };
  lineItems: Array<{
    description: string;
    amount: number;
    type?: 'subscription' | 'setup' | 'addon';
  }>;
  subtotal: number;
  discount?: { code: string; amount: number } | null;
  total: number;
  currency: string;
  formatted: {
    subtotal: string;
    total: string;
  };
  paymentMethod: {
    type: string;
    brand: string | null;
    last4: string;
    expiryMonth?: number | null;
    expiryYear?: number | null;
  } | null;
  paymentId: string;
  paymentType: 'SUBSCRIPTION' | 'RENEWAL' | 'UPGRADE' | 'SETUP_FEE' | 'ADDON' | 'MANUAL' | 'ORGANIZATION';
  paymentMode: string | null;
  paymentDescription: string | null;
  stripePaymentIntentId: string | null;
  stripeReceiptUrl: string | null;
}

export default subscriptionService;

